import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Eye, CreditCard as Edit, Trash2, Clock, CheckCircle, XCircle, Calendar, Image, Video, Users } from 'lucide-react';

interface Post {
  id: string;
  title: string;
  content: string;
  client: string;
  platforms: string[];
  status: 'draft' | 'pending_design' | 'design_in_progress' | 'pending_review' | 'approved' | 'rejected' | 'scheduled' | 'published';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduled_time?: string;
  created_by: string;
  assigned_designer?: string;
  assigned_reviewer?: string;
  media_count: number;
  post_type: 'image' | 'video' | 'carousel' | 'story';
  created_at: string;
}

export default function PostManager() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    // Mock data
    setPosts([
      {
        id: '1',
        title: 'New Product Launch Campaign',
        content: 'Exciting news! Our latest innovation is here...',
        client: 'TechCorp Solutions',
        platforms: ['instagram', 'facebook', 'linkedin'],
        status: 'pending_review',
        priority: 'high',
        scheduled_time: '2024-03-15T10:00:00Z',
        created_by: 'John Doe',
        assigned_designer: 'Sarah Wilson',
        assigned_reviewer: 'Mike Johnson',
        media_count: 3,
        post_type: 'carousel',
        created_at: '2024-03-10T09:00:00Z'
      },
      {
        id: '2',
        title: 'Behind the Scenes Video',
        content: 'Take a look at our creative process...',
        client: 'Fashion Forward',
        platforms: ['instagram', 'tiktok'],
        status: 'design_in_progress',
        priority: 'medium',
        created_by: 'Jane Smith',
        assigned_designer: 'Alex Chen',
        media_count: 1,
        post_type: 'video',
        created_at: '2024-03-09T14:30:00Z'
      },
      {
        id: '3',
        title: 'Weekly Menu Special',
        content: 'This week\'s chef special features...',
        client: 'Healthy Eats',
        platforms: ['instagram', 'facebook'],
        status: 'approved',
        priority: 'medium',
        scheduled_time: '2024-03-12T12:00:00Z',
        created_by: 'Tom Brown',
        assigned_reviewer: 'Lisa Davis',
        media_count: 2,
        post_type: 'image',
        created_at: '2024-03-08T11:15:00Z'
      }
    ]);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending_design': return 'bg-yellow-100 text-yellow-800';
      case 'design_in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending_review': return 'bg-orange-100 text-orange-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      case 'published': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'carousel': return <Image className="h-4 w-4" />;
      case 'story': return <Clock className="h-4 w-4" />;
      default: return <Image className="h-4 w-4" />;
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Post Manager</h1>
          <p className="text-gray-600">Create, manage, and track social media posts</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Post
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending_design">Pending Design</option>
          <option value="design_in_progress">Design in Progress</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
        </select>
      </div>

      {/* Posts List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="divide-y divide-gray-200">
          {filteredPosts.map((post) => (
            <div key={post.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${getPriorityColor(post.priority)}`} />
                    <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                    <div className="flex items-center space-x-1">
                      {getPostTypeIcon(post.post_type)}
                      <span className="text-sm text-gray-500">{post.media_count} media</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-3 line-clamp-2">{post.content}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {post.client}
                    </span>
                    <span>By {post.created_by}</span>
                    {post.scheduled_time && (
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(post.scheduled_time).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    {post.platforms.map((platform) => (
                      <span key={platform} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {platform}
                      </span>
                    ))}
                  </div>
                  
                  {(post.assigned_designer || post.assigned_reviewer) && (
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {post.assigned_designer && (
                        <span>Designer: {post.assigned_designer}</span>
                      )}
                      {post.assigned_reviewer && (
                        <span>Reviewer: {post.assigned_reviewer}</span>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-3 ml-4">
                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(post.status)}`}>
                    {post.status.replace('_', ' ')}
                  </span>
                  
                  <div className="flex items-center space-x-1">
                    <button className="p-2 text-gray-400 hover:text-blue-600">
                      <Eye className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-blue-600">
                      <Edit className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-red-600">
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Post</h2>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter post title..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option>Select client...</option>
                    <option>TechCorp Solutions</option>
                    <option>Fashion Forward</option>
                    <option>Healthy Eats</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Write your post content..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Post Type</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>Image</option>
                      <option>Video</option>
                      <option>Carousel</option>
                      <option>Story</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Platforms</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Instagram', 'Facebook', 'Twitter', 'LinkedIn', 'TikTok', 'YouTube'].map((platform) => (
                      <label key={platform} className="flex items-center space-x-2">
                        <input type="checkbox" className="rounded border-gray-300" />
                        <span className="text-sm">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Post
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}